import { useEffect, useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';
import moment from 'moment'; // npm install moment
import { useReduxStore } from '../hooks/useReduxStore';
import { GetMySubscription } from '../store/Actions/action';

export default function SubscriptionTable() {
  const {getState,dispatch}=useReduxStore()
  const {mySubscriptions,loading}=getState("AUTH")
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
 const fetchSubscriptions =  () => {
         dispatch(GetMySubscription({}))
    };
  useEffect(() => {
    // Simulating API fetch with delay
    fetchSubscriptions();
  }, []);

  const filteredData = mySubscriptions?.filter(sub => {
    const name = sub.planId?.name || 'Unknown';
    const email = sub.status || '';
    return (
      name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      email.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const onClickBtn = () =>{ 
    navigate('/pricing');
  }

  const getFormattedDate = (isoDate) => {
    return isoDate ? moment(isoDate).format('MMMM DD, YYYY') : 'N/A';
  };

  return (
    <div className="min-h-screen p-4 py-4 md:py-8">
      {/* Search Input and Manage Button */}
      <div className="mb-6 md:mb-8 w-full flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-10">
        <div className="relative h-12 md:h-14 w-full flex-1">
          <MagnifyingGlassIcon className="w-5 h-5 md:w-6 md:h-6 absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search subscriptions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full h-12 md:h-14 pl-10 md:pl-12 pr-8 rounded-lg border-2 border-borderGray bg-[#384355] text-white placeholder-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm md:text-base"
          />
        </div>
        <div onClick={onClickBtn} className="text-sm md:text-base text-white font-medium bg-primaryBlue px-6 md:px-8 py-2 md:py-3 rounded-sm h-12 justify-center items-center flex hover:bg-primaryBlue/80 transition cursor-pointer w-full sm:w-auto whitespace-nowrap">
          Manage Subscription
        </div>
      </div>

      {/* Mobile Card View (Visible on small screens) */}
      <div className="block sm:hidden space-y-4">
        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse h-32 bg-gray-800/50 rounded-lg border border-borderGray"></div>
            ))}
          </div>
        ) : filteredData?.length === 0 ? (
          <div className="text-center py-12 text-gray-300 text-sm border-2 border-borderGray rounded-md bg-transparent">
            No subscription data found.
          </div>
        ) : (
          filteredData?.map((sub, idx) => {
            const plan = sub.planId || {};
            const stripeSub = sub.stripeSubscription || {};
            const isCancelled = sub.cancelAtPeriodEnd;
            const status = isCancelled ? 'Cancelled' : sub.status;
            const endDate = getFormattedDate(sub.currentPeriodEnd);
            const paymentStatus = stripeSub?.latest_invoice ? 'Paid' : 'Unpaid';

            return (
              <div key={idx} className="bg-[#384355]/50 border border-borderGray rounded-lg p-4 text-white space-y-3">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-lg">{plan.name}</h3>
                    <p className="text-sm text-gray-300">${(plan.price || 0)}</p>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-1 rounded ${
                    status === 'active'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {status}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-2 text-sm border-t border-gray-600 pt-3">
                  <div>
                    <p className="text-gray-400 text-xs">Renew Date</p>
                    <p>{!isCancelled ? endDate : '--'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs">Payment</p>
                    <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded mt-0.5 ${
                      paymentStatus === 'Paid'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {paymentStatus}
                    </span>
                  </div>
                  {isCancelled && (
                    <div className="col-span-2">
                       <p className="text-gray-400 text-xs">Ends on</p>
                       <p>{endDate}</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Desktop Table View (Hidden on small screens) */}
      <div className="hidden sm:block overflow-x-auto">
        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse h-12 bg-gray-100 rounded"></div>
            ))}
          </div>
        ) : filteredData?.length === 0 ? (
          <div className="text-center py-12 text-gray-300 text-sm border-2 border-borderGray rounded-md bg-transparent">
            No subscription data found.
          </div>
        ) : (
          <table className="min-w-full text-sm border border-gray-300 bg-transparent shadow-sm rounded-lg">
            <thead>
              <tr className="bg-[#384355] text-left text-white">
                <th className="py-5 md:text-base px-5 border-2 border-borderGray">Plan</th>
                <th className="py-5 md:text-base px-5 border-2 border-borderGray">Status</th>
                <th className="py-5 md:text-base px-5 border-2 border-borderGray">Price</th>
                <th className="py-5 md:text-base px-5 border-2 border-borderGray">Renew Date</th>
                <th className="py-5 md:text-base px-5 border-2 border-borderGray">Payment</th>
                {/* <th className="py-6 md:text-lg px-5 border-2 border-borderGray text-center">Action</th> */}
              </tr>
            </thead>
         <tbody>
  {filteredData?.map((sub, idx) => {
    const plan = sub.planId || {};
    const stripeSub = sub.stripeSubscription || {};
    const isCancelled = sub.cancelAtPeriodEnd;
    const status = isCancelled ? 'Cancelled' : sub.status;
    const endDate = getFormattedDate(sub.currentPeriodEnd);
    const paymentStatus = stripeSub?.latest_invoice ? 'Paid' : 'Unpaid';

    return (
      <tr key={idx} className="bg-transparent hover:bg-gray-50/10">
        <td className="py-10 px-5 border-2 border-borderGray text-white font-medium">{plan.name}</td>

        {/* Status + optional End On date */}
        <td className="py-10 px-5 border-2 border-borderGray">
          <div className="flex flex-col">
            <span className={`text-xs font-semibold px-2 py-1 rounded w-fit ${
              status === 'active'
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-700'
            }`}>
              {status}
            </span>
            {isCancelled && (
              <span className="text-xs text-gray-300 mt-1">
                Ends on {endDate}
              </span>
            )}
          </div>
        </td>

        {/* Price */}
        <td className="py-10 px-5 border-2 border-borderGray text-white">${(plan.price || 0) }</td>

        {/* Renew Date (only show if NOT cancelled) */}
        <td className="py-10 px-5 border-2 border-borderGray text-white">
          {!isCancelled ? endDate : '--'}
        </td>

        {/* Payment Status */}
        <td className="py-10 px-5 border-2 border-borderGray">
          <span className={`text-xs font-semibold px-2 py-1 rounded ${
            paymentStatus === 'Paid'
              ? 'bg-green-100 text-green-700'
              : 'bg-yellow-100 text-yellow-800'
          }`}>
            {paymentStatus}
          </span>
        </td>

        {/* Button */}
        {/* <td className="py-4 px-5 border-2 border-borderGray text-center">
          <button
            onClick={onClickBtn}
            className="text-sm bg-primaryBlue text-white px-3 py-1 rounded hover:bg-primaryBlue/80"
          >
            {isCancelled ? 'Renew' : 'Manage'}
          </button>
        </td> */}
      </tr>
    );
  })}
</tbody>

          </table>
        )}
      </div>
    </div>
  );
}